import { redactUrl } from '@/utils/analytics/redact-url'
import { publicEnv } from '@/utils/public-env'

// Typed access to window.gtag. Safe to import in non-browser code — calls are
// no-ops when the global gtag isn't loaded (dev, prerender, or when GA is
// disabled by missing NEXT_PUBLIC_GOOGLE_ANALYTICS_ID).

type GtagCommand = 'config' | 'set' | 'event' | 'consent' | 'js'
type GtagParams = Record<string, unknown>

declare global {
  interface Window {
    gtag?: (command: GtagCommand, ...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

const PAGE_PARAMS = ['page_location', 'page_path', 'page_referrer'] as const

/**
 * GA has no before-send hook, so every event leaves through here: page params
 * a caller passes are redacted, and when the address bar or referrer holds a
 * link secret the redacted value is passed explicitly — otherwise gtag reads
 * `document.location` / `document.referrer` raw.
 */
function redactPageParams(params: unknown): GtagParams {
  const out: GtagParams = { ...(params as GtagParams | undefined) }
  for (const key of PAGE_PARAMS) {
    const value = out[key]
    if (typeof value === 'string') out[key] = redactUrl(value)
  }
  const ambient = {
    page_location: window.location.href,
    page_referrer: document.referrer
  }
  for (const [key, value] of Object.entries(ambient)) {
    const redacted = redactUrl(value)
    if (out[key] === undefined && redacted !== value) out[key] = redacted
  }
  return out
}

export function gtag(command: GtagCommand, ...args: unknown[]): void {
  if (typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return
  if (command === 'event') {
    window.gtag(command, args[0], redactPageParams(args[1]))
    return
  }
  window.gtag(command, ...args)
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(publicEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS_ID'))
}

export function gaId(): string | undefined {
  return publicEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS_ID')
}

export type { GtagParams }
